import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Sparkles, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * BetaGatedModal — surfaces the warm "we're in beta" copy when the
 * backend rejects an auth attempt with `error: "beta_gated"`. The
 * payload from `beta_allowlist.beta_gate_error()` carries:
 *   { title, message, cta_label }
 *
 * The modal shows the founder-written copy plus an inline waitlist
 * signup form so the rejection becomes a softer "please come back"
 * moment instead of a dead end.
 *
 * Note: the beta gate is currently disabled (BETA_GATE_ENABLED=0),
 * so this modal only renders if an admin re-enables gating later.
 */
const BetaGatedModal = ({ open, onClose, payload, prefilledEmail = "" }) => {
  const [email, setEmail] = useState(prefilledEmail);
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const title = payload?.title || "Sorry babes!";
  const message =
    payload?.message ||
    "We're in beta right now. Sign up for an alert when your place opens up.";
  const ctaLabel = payload?.cta_label || "Join the waitlist";

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/waitlist/signup`,
        {
          email: email.trim().toLowerCase(),
          name: name.trim() || undefined,
          platform: "web",
          source: "beta_gate",
        },
      );
      setDone(true);
      toast.success("You're on the list — we'll be in touch soon. 💋");
    } catch {
      toast.error("Couldn't add you. Try again in a sec.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-[80] flex items-center justify-center px-4 py-8 overflow-y-auto bg-[#2C2123]/65 backdrop-blur-sm"
        data-testid="beta-gated-modal-root"
        onClick={(e) => {
          if (e.target === e.currentTarget && !submitting) onClose?.();
        }}
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 10 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.96, opacity: 0, y: 10 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="relative max-w-md w-full bg-white rounded-3xl shadow-2xl border border-[#EADDD7] p-7 sm:p-8"
        >
          <button
            type="button"
            onClick={() => !submitting && onClose?.()}
            aria-label="Close"
            className="absolute top-4 right-4 h-8 w-8 rounded-full text-[#8A7D82] hover:text-[#2C2123] hover:bg-[#F2E8E9] transition-colors flex items-center justify-center"
            data-testid="beta-gated-modal-close"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="flex items-start gap-3 mb-4">
            <div className="shrink-0 h-11 w-11 rounded-full bg-[#F2E8E9] flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-[#9B6A6C]" />
            </div>
            <div className="flex-1 pt-1">
              <h3
                className="text-2xl font-light text-[#2C2123] leading-tight"
                data-testid="beta-gated-modal-title"
              >
                {title}
              </h3>
            </div>
          </div>

          <p
            className="text-[#5C4B51] text-sm leading-relaxed mb-5"
            data-testid="beta-gated-modal-message"
          >
            {message}
          </p>

          {done ? (
            <div
              className="flex items-start gap-3 p-4 bg-[#FDF6F3] border border-[#F2E8E9] rounded-xl text-sm text-[#5D4050]"
              data-testid="beta-gated-modal-success"
            >
              <Check className="h-4 w-4 text-[#9B6A6C] shrink-0 mt-0.5" />
              <div>
                <strong className="text-[#2C2123]">You're on the list.</strong>{" "}
                We'll send a heads-up the moment your spot opens.
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-2.5">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full bg-white border border-[#EADDD7] rounded-xl px-4 py-3 text-[#2C2123] text-sm placeholder:text-[#A8959B] focus:outline-none focus:border-[#9B6A6C] transition-colors"
                data-testid="beta-gated-modal-email"
              />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="First name (optional)"
                maxLength={120}
                className="w-full bg-white border border-[#EADDD7] rounded-xl px-4 py-3 text-[#2C2123] text-sm placeholder:text-[#A8959B] focus:outline-none focus:border-[#9B6A6C] transition-colors"
                data-testid="beta-gated-modal-name"
              />
              <button
                type="submit"
                disabled={submitting || !email.trim()}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#9B6A6C] hover:bg-[#7d5455] text-white rounded-xl px-5 py-3 text-sm font-medium transition-colors disabled:opacity-50"
                data-testid="beta-gated-modal-submit"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {ctaLabel}
              </button>
            </form>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BetaGatedModal;
